import { parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js';

// Country used to interpret numbers entered without the international prefix
const DEFAULT_COUNTRY = 'CA';

/**
 * Parse a phone number, with or without the leading "+". Numbers without it
 * are tried as a national number of the default country, then as an
 * international number. Falls back to the raw parse when none is valid.
 *
 * @param     {String} value   The phone number to parse
 *
 * @return    {PhoneNumber|undefined}
 */
const parse = (value) => {
    const raw    = (value || '').trim();
    const parsed = parsePhoneNumberFromString(raw);

    if ((parsed && parsed.isValid()) || raw === '' || raw.startsWith('+')) {
        return parsed;
    }

    const candidates = [
        parsePhoneNumberFromString(raw, DEFAULT_COUNTRY),
        parsePhoneNumberFromString(`+${raw}`)
    ];

    return candidates.find(candidate => candidate && candidate.isValid()) || parsed;
};

/**
 * Whether the given value is a valid phone number, landline or mobile.
 *
 * @param     {String} value   The phone number to validate
 *
 * @return    {Boolean}
 */
export const isValidPhoneNumber = (value) => {
    const parsed = parse(value);

    return !!parsed && parsed.isValid();
};

/**
 * Normalize a phone number into E.164 format, returning the original
 * value when it cannot be parsed so the API can report the error.
 *
 * @param     {String} value   The phone number to normalize
 *
 * @return    {String}
 */
export const toE164 = (value) => {
    const parsed = parse(value);

    return parsed ? parsed.format('E.164') : value;
};

/**
 * Why the given value is not a usable phone number, null when it is.
 *
 * @param     {String} value   The phone number to check
 *
 * @return    {String|null}    TOO_SHORT, TOO_LONG, INVALID_LENGTH, INVALID_COUNTRY,
 *                             NOT_A_NUMBER, UNKNOWN_NUMBER or null
 */
export const phoneNumberError = (value) => {
    const text = (value || '').trim();

    if (text === '') {
        return 'NOT_A_NUMBER';
    }

    if (isValidPhoneNumber(text)) {
        return null;
    }

    return validatePhoneNumberLength(text, DEFAULT_COUNTRY) || 'UNKNOWN_NUMBER';
};

/**
 * Format a phone number for display: "+1 (819) 380-2808" for North American
 * numbers, the international format ("+44 7401 240227") for the others.
 * Returns the original value when it is not a valid phone number.
 *
 * @param     {String} value   The phone number to format
 *
 * @return    {String}
 */
export const formatPhoneNumber = (value) => {
    const parsed = parse(value);

    if (!parsed || !parsed.isValid()) {
        return value;
    }

    if (parsed.countryCallingCode === '1') {
        return `+1 ${parsed.formatNational()}`;
    }

    return parsed.formatInternational();
};

/**
 * Format a label that may be a raw phone number (contacts without a name
 * use their number as name). Anything not starting with "+" is left as is.
 *
 * @param     {String} value   The label to format
 *
 * @return    {String}
 */
export const formatPhoneLabel = (value) => {
    return typeof value === 'string' && value.trim().startsWith('+') ? formatPhoneNumber(value.trim()) : value;
};
